"use client"

import * as React from "react"

export type Theme = "light" | "dark"

export const THEME_COOKIE = "ennex-theme"

interface ThemeContextValue {
  theme: Theme
  /** alias of `theme` — we don't use system theme, so they're identical */
  resolvedTheme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

function applyTheme(theme: Theme) {
  const root = document.documentElement
  root.classList.remove("light", "dark")
  root.classList.add(theme)
  root.style.colorScheme = theme
}

function persistTheme(theme: Theme) {
  try {
    // cookie so the server layout can render the right class with no flash + no inline <script>
    document.cookie = `${THEME_COOKIE}=${theme};path=/;max-age=31536000;samesite=lax`
  } catch {
    // ignore
  }
}

/**
 * Lightweight theme provider (replaces next-themes). No inline <script> anywhere —
 * React 19 / Next 16 reject script tags rendered through JSX. Instead the chosen
 * theme is stored in a cookie and the server layout sets the <html> class, so the
 * initial paint is already correct (no flash, no hydration mismatch).
 */
export function ThemeProvider({
  children,
  defaultTheme = "light",
}: {
  children: React.ReactNode
  /** resolved server-side from the theme cookie */
  defaultTheme?: Theme
}) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme)

  // The server layout renders the default template (light). Re-read the cookie
  // once on mount so a user who explicitly picked dark keeps it across reloads,
  // independent of whether the layout render was cached.
  React.useEffect(() => {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${THEME_COOKIE}=(light|dark)`))
    const saved = m?.[1] as Theme | undefined
    if (saved && saved !== defaultTheme) {
      setThemeState(saved)
      applyTheme(saved)
    }
  }, [defaultTheme])

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next)
    persistTheme(next)
    applyTheme(next)
  }, [])

  const toggleTheme = React.useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark"
      persistTheme(next)
      applyTheme(next)
      return next
    })
  }, [])

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme: theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) {
    // graceful fallback so consumers never crash outside the provider
    return {
      theme: "light",
      resolvedTheme: "light",
      setTheme: () => {},
      toggleTheme: () => {},
    }
  }
  return ctx
}
